/**
 * ─── Home Page API Service ───
 *
 * All API calls related to the public Home page live here.
 * Keeps components free of fetch logic and makes endpoints easy to update.
 */

import { API_URL } from "../config";

const BASE_URL = `${API_URL}/api`;

/**
 * Fetch all visible posts / upcoming appointments.
 * @returns {Promise<Array>} list of post objects
 */
export async function fetchPosts() {
  const res = await fetch(`${BASE_URL}/posts`);
  if (!res.ok) throw new Error("Failed to load posts");
  return res.json();
}

/**
 * Submit a booking for a given post.
 * @param {string|number} postId
 * @param {Object}        bookingData  { student_name, student_email, age_range, tajweed_level }
 * @returns {Promise<Object>} server response
 */
export async function submitBooking(postId, bookingData) {
  const res = await fetch(`${BASE_URL}/posts/${postId}/bookings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include", // send httpOnly cookie if logged-in
    body: JSON.stringify(bookingData),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || "Påmelding feilet");
  }
  return res.json();
}

/**
 * Fetch current user's bookings.
 * @returns {Promise<Array>} list of booking objects
 */
export async function fetchMyBookings() {
  const res = await fetch(`${BASE_URL}/my-bookings`, { credentials: "include" });
  if (!res.ok) return [];
  return res.json();
}

/**
 * Update a booking (e.g. change tajweed level).
 * @param {number} bookingId
 * @param {Object} data  { tajweed_level }
 */
export async function updateBooking(bookingId, data) {
  const res = await fetch(`${BASE_URL}/bookings/${bookingId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => null);
    throw new Error(d?.error || "Oppdatering feilet");
  }
  return res.json();
}

/**
 * Cancel a booking.
 * @param {number} bookingId
 */
export async function cancelBooking(bookingId) {
  const res = await fetch(`${BASE_URL}/bookings/${bookingId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const d = await res.json().catch(() => null);
    throw new Error(d?.error || "Avbestilling feilet");
  }
  return res.json();
}

/**
 * Fetch all visible events.
 * @returns {Promise<Array>} list of event objects
 */
export async function fetchEvents() {
  const res = await fetch(`${BASE_URL}/events`);
  if (!res.ok) throw new Error("Failed to load events");
  return res.json();
}

// ─── Seminars ───

export async function fetchSeminarer() {
  const res = await fetch(`${BASE_URL}/seminarer`);
  if (!res.ok) throw new Error("Failed to load seminars");
  return res.json();
}

export async function submitSeminarRegistration(seminarId, data) {
  const res = await fetch(`${BASE_URL}/seminarer/${seminarId}/registrations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => null);
    throw new Error(d?.error || "Påmelding feilet");
  }
  return res.json();
}

export async function fetchMySeminarRegistrations() {
  const res = await fetch(`${BASE_URL}/my-seminar-registrations`, { credentials: "include" });
  if (!res.ok) return [];
  return res.json();
}

export async function cancelSeminarRegistration(id) {
  const res = await fetch(`${BASE_URL}/seminar-registrations/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const d = await res.json().catch(() => null);
    throw new Error(d?.error || "Avbestilling feilet");
  }
  return res.json();
}
